import settings from '../../config';
import { show_message } from '../../lib/flash.ts';
import { t9n } from '../../lib/translate';
import { TS } from '../../config/translation-keys.js';
import { default_context, server_request } from './request-machine';
import { debug } from '../../devtools';
import session from '../../lib/session';
import Events from '../../lib/events';

const in_progress = {};

const auth_endpoints = ['/logout', '/login'];

export const unauthorized_endpoints = [
  ...auth_endpoints,
  '/config',
  '/config/merchant',
  '/merchant_style',
  '/available_merchants',
  '/merchant_for_code',
  '/confirm',
  '/confirm_email',
  '/forgot_password',
  '/playground',
  '/register',
  '/resend_confirm_email',
  '/merchant_style',
  '/order/share',
  '/order/shared',
  '/reset-password',
  '/share-order',
  '/verify_email',
  '/verify_phone',
  '/order/verify_email',
  '/invite',
];

const host = () => settings.api_host;

const version = () => settings.api_version;

const auth_token = () => session.value().token;

const api_config_version = () => session.value().api_config.config_version;

const merchant_uuid = () => session.value().merchant.merchant_uuid;

const merchant_config_version = () => session.value().merchant.config_version;

const branding_version = () => session.value().branding.vars.config_version;

export const build_url = context => {
  context.endpoint = context.api_request
    ? context.versioned
      ? `${host()}/api/v${version()}${context.endpoint}`
      : `${host()}/api${context.endpoint}`
    : `${host()}${context.endpoint}`;
};

const is_unauthorized = endpoint => {
  return (
    unauthorized_endpoints.includes(endpoint) ||
    endpoint.includes('/cv/worker/') ||
    endpoint.includes('/map/workers/') ||
    endpoint.includes('/order/share/') ||
    endpoint.includes('/order/shared/') ||
    endpoint.includes('/workers_map/')
  );
};

const without_merchant_config = endpoint => {
  return (
    endpoint.startsWith('/profile') ||
    endpoint.startsWith('/notifications') ||
    endpoint.startsWith('/merchants_for_registering') ||
    endpoint.startsWith('/register_for_merchant') ||
    endpoint.startsWith('/tutorials')
  );
};

export const add_auth_headers = context => {
  if (!is_unauthorized(context.endpoint)) {
    const token = auth_token();

    if (!!token) {
      context.headers['X-Auth-Token'] = token;

      if (!!session.value().api_config) {
        context.headers['WP-Config-Version'] = api_config_version();
      }

      if (
        !!session.value().merchant &&
        !without_merchant_config(context.endpoint)
      ) {
        context.headers['WP-Merchant-UUID'] = merchant_uuid();
        context.headers[
          'WP-Merchant-Config-Version'
        ] = merchant_config_version();
      }

      if (
        !!session.value().branding &&
        !without_merchant_config(context.endpoint)
      ) {
        context.headers['WP-Merchant-Web-Style-Version'] = branding_version();
      }
    } else {
      // We hit this path with good intentions when the token got deleted after
      // an error in reading it, or accessing the API using an expired token.
      // Still, we need to debug if we are in this branch, because it is
      // failing silently in case we miss declaring our api request as
      // `{..., authenticated: false }`, or forget to put the given endpoint
      // into the list of unauthenticated endpoints, so it can produce confusing
      // errors. THINK ABOUT: Could we make it somehow behaving better?
      debug(
        `No auth token available in add_auth_headers (api-request, context: ${context}).`
      );

      return Events.dispatch('unauthenticated', { detail: context });
    }
  }
};

const build_request_context = options => {
  const context = {
    ...default_context,
    ...options,
  };

  if (context.authenticated) {
    add_auth_headers(context);
  }
  build_url(context);

  return context;
};

// const stop_service = service => {
//   service.send('cancel');
//   service.stop();
// };

export const api_request = async options => {
  const context = build_request_context(options);
  const request_key = `${context.method} ${context.endpoint}`;
  // console.log('api_request: ', `${context.method} ${options.endpoint}`)
  let result = null;

  if (request_key in in_progress) {
    return result;
  }

  const service = server_request(context)
    // .onTransition(state => {
    //   debug(`state of request for ${context.endpoint}:`, state.value);
    //   debug(`context of request for ${context.endpoint}`, state.context);
    // })
    .start();

  in_progress[request_key] = service;

  service.send('fetch');

  result = new Promise((resolve, reject) => {
    service.onDone(() => {
      delete in_progress[request_key];

      if (service.state.value === 'success') {
        return resolve(service.state.context.result);
      } else if (service.state.value === 'failed') {
        return handle_service_error(
          context,
          reject,
          service.state.context.error
        );
      }

      service.stop();
    });
  });

  return await result;
};

// All errors comes in the form of a list of errors,
// but currently we always have only one error on the
// list, so this function does not handle the case
// of multiple errors in one API response.
const handle_service_error = (context, reject, error) => {
  if (!context.handle_errors) {
    return reject(error);
  }

  let message = null;
  let first_error = null;
  let error_code = null;
  let params = [];

  if ('details' in error && Object.keys(error.details).length > 0) {
    // APIError or APIClientError
    first_error = error.details.error_data[0];
    error_code = first_error.code;
  } else if (error.length > 0 && 'data' in error[0]) {
    // Error message from the server. Example:
    // [{"code":"error_cannot_target_yourself","data":{}}]
    first_error = error[0];
    error_code = first_error.code;
  }

  if ('params' in first_error.data) {
    // Error message from the server with params. Example:
    // [{code: 'error_order_amount_smaller_than_the_minimum',data: {params: ['€', 15]}}]
    params = first_error.data.params;
  }

  message = t9n(error_code, ...params);

  // DO NOT SHOW `field_name` in error message, because
  // that will mess up authentication error messages.
  // if ('field_name' in first_error.data) {
  //   // Error message from the server with field name. Example:
  //   // [{"code":"error_login_invalid_credentials","data":{"field_name":"password"}}]
  //   const field_name = first_error.data.field_name;

  //   message = `${field_name
  //     .replace(/_/g, ' ')
  //     .replace(/(^.?)/, (match, s1) => s1.toUpperCase())}: ${message}`;
  // }

  if ('message' in first_error.data) {
    // APIError or APIClientError
    message = `${message}: ${first_error.data.message}`;
  }

  show_message(message, 'action');
};
